// Gestione delle fonti, connected: the catalogue from useSources; the
// checkboxes are the filter of the lists, kept in ?sources= only; the pin
// of an organisation opens the folder chooser, which pins the source with
// useFollowSources (PUT /me/sources) and then puts it in the chosen feeds
// (PATCH /me/feeds/{id}) or in a new one (POST /me/feeds). The panel
// component (src/components/sidebar/panels.tsx) only sees the tree, the
// selection and the callbacks.
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import type { Source, SubSource } from "../../data/mock";
import { RemoveFilterChip } from "../../components/news/NewsCard";
import { FontiPanel, type BulkMode, type PanelError } from "../../components/sidebar/panels";
import {
  addToFolder,
  feedErrorMessage,
  folderMembership,
  foldersHolding,
  nameProblem,
  newFeed,
  removeFromFolder,
  sortFeeds,
  type FeedChange,
  type NewFeed,
  type UserFeed,
} from "../feeds/adapt";
import { useChangeFeed, useCreateFeed, useFeeds } from "../feeds/api";
import { sittingsViewLabel } from "../parliament/adapt";
import { FolderChooser, type FolderChoice, type FolderChooserError } from "../feeds/FolderChooser";
import {
  activeKeys,
  bulkMode,
  favouriteKeys,
  favouriteSelection,
  FOLLOW_FAILED,
  followErrorMessage,
  heldKeys,
  keysAfterToggle,
  missingKeys,
  nodeLabel,
  otherSelection,
  subtreeKeys,
  toSelection,
  toSourceTree,
} from "./adapt";
import { useFollowSources, useSources, type FollowChange } from "./api";
import { readNewsParams, withNewsParams } from "./params";

/** The list without the given keys; the entries left empty go. */
function withoutKeys(list: FollowChange[], drop: Set<string>): FollowChange[] {
  const keep = (keys: string[] | undefined) => keys?.filter((k) => !drop.has(k));
  return list.flatMap((change) => compact({ add: keep(change.add), remove: keep(change.remove) }));
}

/** A change with its empty sides cut off, as a list of zero or one entry. */
function compact(change: FollowChange): FollowChange[] {
  const out: FollowChange = {};
  if (change.add && change.add.length > 0) out.add = change.add;
  if (change.remove && change.remove.length > 0) out.remove = change.remove;
  return out.add || out.remove ? [out] : [];
}

/** What a query gives the panel: its data (even when a refetch failed), the wait, or the failure. */
const statusOf = (q: { data: unknown; isError: boolean; isFetching: boolean }) =>
  q.data !== undefined ? "ready" : q.isError && !q.isFetching ? "error" : "loading";

export function SourcesPanel({ onCollapse }: { onCollapse: () => void }) {
  const [search, setSearch] = useSearchParams();
  const sources = useSources();
  const follow = useFollowSources();
  const feedsQuery = useFeeds();
  const change = useChangeFeed();
  const create = useCreateFeed();

  const tree = useMemo(() => (sources.data ? toSourceTree(sources.data) : null), [sources.data]);
  const favourites = useMemo(() => (sources.data ? favouriteKeys(sources.data) : []), [sources.data]);
  const allActive = useMemo(() => (tree ? activeKeys(tree) : []), [tree]);
  const feeds = useMemo(() => (feedsQuery.data ? sortFeeds(feedsQuery.data.feeds) : null), [feedsQuery.data]);

  // the checkboxes: the filter of ?sources= (null: everything ticked)
  const filter = readNewsParams(search).sources;
  const selection = useMemo(() => (tree ? toSelection(tree, filter ?? allActive) : {}), [tree, filter, allActive]);
  const favouriteKeysInOrder = useMemo(() => (tree ? favouriteSelection(tree, favourites) : []), [tree, favourites]);
  const otherKeysInOrder = useMemo(() => (tree ? otherSelection(tree, favourites) : []), [tree, favourites]);
  const mode = bulkMode(filter, favouriteKeysInOrder, allActive);
  // «preferiti» and «altri» have an effect only when both sets exist
  const split = favouriteKeysInOrder.length > 0 && otherKeysInOrder.length > 0;
  const bulkAvailable: Record<BulkMode, boolean> = { all: true, none: true, favorites: split, others: split };

  const setFilter = (next: string[] | null) => setSearch(withNewsParams(search, { sources: next }));
  const onSelect = (item: Source | SubSource, checked: boolean) =>
    setFilter(keysAfterToggle(filter, allActive, item, checked));
  const onBulk = (next: BulkMode) => {
    if (next === "all") setFilter(null);
    else if (next === "none") setFilter([]);
    else if (next === "favorites") setFilter(favouriteKeysInOrder);
    else setFilter(otherKeysInOrder);
  };

  // the follow changes the api refused and that nothing has overridden since (sent again by «Riprova»)
  const [rejected, setRejected] = useState<FollowChange[]>([]);
  // a change the api answered 422 to: nothing to retry, the catalogue is read again
  const [refused, setRefused] = useState<string | null>(null);
  // for each key, the sequence number of the last change that touched it
  const lastTouch = useRef(new Map<string, number>());
  const sequence = useRef(0);

  // mutateAsync, not mutate: the per-call callbacks of mutate only fire for the last call,
  // and several changes can be in flight at once. Resolves to whether the api accepted it.
  const send = (followChange: FollowChange): Promise<boolean> => {
    const touched = [...(followChange.add ?? []), ...(followChange.remove ?? [])];
    sequence.current += 1;
    const mine = sequence.current;
    for (const key of touched) lastTouch.current.set(key, mine);
    // a new choice on a key supersedes any refused one on it (this is also how a retry takes its entry out)
    setRejected((list) => withoutKeys(list, new Set(touched)));
    setRefused(null);
    return follow.mutateAsync(followChange).then(
      () => true,
      (error: unknown) => {
        const { message, retry } = followErrorMessage(error);
        if (!retry) {
          setRefused(message);
          return false;
        }
        // keep only the keys nobody has chosen again while this change was in flight
        const current = (keys: string[] | undefined) => keys?.filter((k) => lastTouch.current.get(k) === mine);
        const kept = { add: current(followChange.add), remove: current(followChange.remove) };
        setRejected((list) => withoutKeys(list, new Set(touched)).concat(compact(kept)));
        return false;
      },
    );
  };

  // the last PATCH of a folder the api refused, with «Riprova» when it can be sent again
  const [folderError, setFolderError] = useState<PanelError | null>(null);
  const patchFolder = (id: string, body: FeedChange) => {
    setFolderError(null);
    change.mutateAsync({ id, change: body }).catch((error: unknown) => {
      const { message, retry } = feedErrorMessage(error, "folder");
      setFolderError({ message, ...(retry ? { onRetry: () => patchFolder(id, body) } : {}) });
    });
  };

  const linkTo = (item: Source | SubSource) => {
    const keys = subtreeKeys(item);
    return keys.length > 0 ? `/news?${withNewsParams(search, { tab: "latest", sources: keys })}` : null;
  };

  const status = sources.isPending ? "loading" : sources.isError ? "error" : "ready";
  const followError: PanelError | null =
    rejected.length > 0
      ? { message: FOLLOW_FAILED, onRetry: () => rejected.forEach((entry) => void send(entry)) }
      : refused
        ? { message: refused }
        : null;

  return (
    <FontiPanel
      onCollapse={onCollapse}
      sources={tree?.active ?? null}
      deleted={tree?.deleted ?? []}
      totalSources={tree?.totalActive ?? null}
      status={status}
      onRetry={() => void sources.refetch()}
      selection={selection}
      onSelect={onSelect}
      bulkMode={mode}
      bulkAvailable={bulkAvailable}
      onBulk={onBulk}
      favoriteChooser={(source, close, setBusy) => (
        <PinChooser
          source={source}
          close={close}
          setBusy={setBusy}
          favourites={favourites}
          feeds={feeds}
          feedsStatus={statusOf(feedsQuery)}
          onRetryFeeds={() => void feedsQuery.refetch()}
          pin={(keys) => send({ add: keys })}
          unpin={(keys) => void send({ remove: keys })}
          patch={patchFolder}
          create={(body) => create.mutateAsync(body)}
        />
      )}
      linkTo={linkTo}
      followError={followError}
      folderError={folderError}
    />
  );
}

type PinChooserProps = {
  source: Source;
  close: () => void;
  /** A call the popover waits for is on its way: the popover does not close under it (Esc, a click outside). */
  setBusy: (busy: boolean) => void;
  favourites: string[];
  feeds: UserFeed[] | null;
  feedsStatus: "loading" | "error" | "ready";
  onRetryFeeds: () => void;
  /** PUT add: resolves to whether the api accepted it. */
  pin: (keys: string[]) => Promise<boolean>;
  /** PUT remove, with the panel's queue. */
  unpin: (keys: string[]) => void;
  /** PATCH /me/feeds/{id}, optimistic, with the panel's warning. */
  patch: (id: string, body: FeedChange) => void;
  /** POST /me/feeds; rejects when refused. */
  create: (body: NewFeed) => Promise<UserFeed>;
};

/**
 * The pin's popover of one organisation: the chooser with the folders that
 * hold the source, and the calls in order: the PUT first, then (only if it
 * was accepted) the PATCHes and the POST. The popover waits for the PUT
 * and for the POST; the PATCHes are optimistic, their failure shows in the
 * panel. A refused POST stays in the popover with «Riprova».
 */
function PinChooser({
  source,
  close,
  setBusy,
  favourites,
  feeds,
  feedsStatus,
  onRetryFeeds,
  pin,
  unpin,
  patch,
  create,
}: PinChooserProps) {
  const [pending, setPendingState] = useState(false);
  const [error, setError] = useState<FolderChooserError | null>(null);
  const setPending = (next: boolean) => {
    setPendingState(next);
    setBusy(next);
  };
  // the mode of the popover is the pin's when it opened: the PUT of «Salva» does not turn it into the management
  const [mode] = useState<"save" | "manage">(() => (source.favorite ? "manage" : "save"));
  const held = heldKeys(source, favourites);
  const missing = missingKeys(source, favourites);
  // what the folders hold: the favourite sections of the source (every section once it is pinned)
  const nodeKeys = mode === "save" ? subtreeKeys(source) : held;
  const membership = feeds ? folderMembership(feeds, held) : {};
  const folders = feeds ? feeds.map((f) => ({ id: f.id, name: f.name, held: membership[f.id] ?? "none" })) : null;
  const leaves = feeds ? foldersHolding(feeds, held).map((f) => f.name) : [];

  const createFolder = (body: NewFeed) => {
    setPending(true);
    setError(null);
    create(body).then(
      () => close(),
      (refusal: unknown) => {
        const { message, retry } = feedErrorMessage(refusal, "create-folder");
        setError({ message, ...(retry ? { onRetry: () => createFolder(body) } : {}) });
        setPending(false);
      },
    );
  };

  const onSave = (choices: FolderChoice[], newFolder: string | null) => {
    setPending(true);
    setError(null);
    const run = async () => {
      if (missing.length > 0 && mode === "save") {
        const accepted = await pin(missing);
        // the panel says why; nothing goes into a folder a source is not pinned for
        if (!accepted) return close();
      }
      // a folder chosen goes to the whole source; one left (also one that held it in part) loses every section
      for (const { id, checked } of choices) {
        const feed = feeds?.find((f) => f.id === id);
        if (!feed) continue;
        const body = checked ? addToFolder(feed, nodeKeys) : removeFromFolder(feed, nodeKeys);
        if (body) patch(feed.id, body);
      }
      if (newFolder) createFolder(newFeed(newFolder, nodeKeys));
      else close();
    };
    void run();
  };

  return (
    <FolderChooser
      mode={mode}
      partly={Boolean(source.favoritePartly)}
      folders={folders}
      foldersStatus={feedsStatus}
      onRetryFolders={onRetryFeeds}
      nameProblem={(name) => nameProblem(feeds ?? [], name)}
      leaves={leaves}
      pending={pending}
      error={error}
      onSave={onSave}
      onAddMissing={() => {
        void pin(missing);
        close();
      }}
      onRemove={() => {
        unpin(held);
        close();
      }}
      onCancel={close}
    />
  );
}

/**
 * «Ultime news · <nome>» over the lists when the filter is exactly one node
 * of the tree (an organisation, a chamber, a section: a clicked name or
 * chip) or the four sources of sittings (the view of /parlamento: «Parlamento
 * · Sedute»), with «Rimuovi il filtro» under it: the lists of every source,
 * the rest of the query string as it is (what «Seleziona tutte» does);
 * nothing otherwise.
 */
export function SourceViewHeading() {
  const [search] = useSearchParams();
  const { sources: keys } = readNewsParams(search);
  const catalogue = useSources();
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  if (!keys) return null;
  const name = sittingsViewLabel(keys) ?? (tree ? nodeLabel(tree, keys) : null);
  if (!name) return null;
  const rest = withNewsParams(search, { sources: null }).toString();
  return (
    <div className="flex flex-col items-start gap-[15px]">
      <h2 className="text-xl leading-7 font-semibold text-brand">Ultime news · {name}</h2>
      <RemoveFilterChip to={{ pathname: "/news", search: rest ? `?${rest}` : "" }} />
    </div>
  );
}
